/**
 * ToolProgress — renders tool_start / tool_end / status chunks as a legible
 * step list with elapsed time (TASK-711). Makes generate_brief's ~180s a
 * sequence of named steps instead of bouncing dots.
 */

import React, { useEffect, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';

export interface ToolProgressEvent {
  /** Stable id per tool invocation (tool name + index is fine). */
  id: string;
  /** Human label, e.g. "Running generate_brief…" or a status line. */
  label: string;
  status: 'running' | 'completed' | 'failed';
  /** Epoch ms when the step started — drives the elapsed counter. */
  startedAt: number;
  /** Epoch ms when the step finished. */
  endedAt?: number;
}

const fmtElapsed = (ms: number): string => {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

export const ToolProgress: React.FC<{ events: ToolProgressEvent[] }> = ({ events }) => {
  const anyRunning = events.some((e) => e.status === 'running');
  const [, setTick] = useState(0);

  // Re-render each second while a step runs so the elapsed counter moves.
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [anyRunning]);

  if (events.length === 0) return null;

  return (
    <div className="space-y-1 rounded-lg border border-border bg-muted/40 px-2.5 py-2" data-testid="tool-progress">
      {events.map((e) => (
        <div key={e.id} className="flex items-center gap-2 text-xs" data-status={e.status}>
          {e.status === 'running' && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />}
          {e.status === 'completed' && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
          {e.status === 'failed' && <X className="h-3.5 w-3.5 shrink-0 text-red-500" />}
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{e.label}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground/70">
            {fmtElapsed((e.endedAt ?? Date.now()) - e.startedAt)}
          </span>
        </div>
      ))}
    </div>
  );
};
